/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningProblem.module.scss';

const FundingGapIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <line
      x1="4"
      y1="26"
      x2="26"
      y2="26"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <rect
      x="6"
      y="18"
      width="4.5"
      height="8"
      rx="1"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '80ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="12.75"
      y="12"
      width="4.5"
      height="14"
      rx="1"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '160ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="19.5"
      y="6"
      width="4.5"
      height="20"
      rx="1"
      strokeDasharray="2.4 2.4"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '240ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const ThinDataIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <line
      x1="5"
      y1="7"
      x2="25"
      y2="7"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="5"
      y1="14"
      x2="22"
      y2="14"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '80ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="5"
      y1="21"
      x2="16"
      y2="21"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '160ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <line
      x1="19"
      y1="21"
      x2="25"
      y2="21"
      strokeDasharray="1.8 2"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '260ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const NoTraceIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <path
      d="M9 4h8l5 5v17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path
      d="M17 4v5h5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '100ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle
      cx="14.5"
      cy="19.5"
      r="3.6"
      strokeDasharray="3 2.6"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '220ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

const OwnershipLayersIcon = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <rect
      x="4"
      y="14"
      width="11"
      height="11"
      rx="1.5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '160ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="10"
      y="8"
      width="11"
      height="11"
      rx="1.5"
      pathLength={100}
      className={styles.iconDraw}
      style={{ transitionDelay: '80ms' }}
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <rect
      x="16"
      y="2"
      width="11"
      height="11"
      rx="1.5"
      pathLength={100}
      className={styles.iconDraw}
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
);

type Problem = {
  num: string;
  icon: ReactNode;
  title: string;
  lead: string;
  detail: string;
};

const PROBLEMS: Problem[] = [
  {
    num: '01',
    icon: <FundingGapIcon />,
    title: 'Most of the market never took funding',
    lead: 'PitchBook and Crunchbase index the companies that announced a round. That is a fraction of what you could buy.',
    detail:
      'The profitable, family-held businesses that never raised file accounts every year and sit outside those platforms. Start a screen from a funding database and you cap your market before looking at a single target.',
  },
  {
    num: '02',
    icon: <ThinDataIcon />,
    title: 'Financial data on private companies runs thin',
    lead: 'Where a private company does appear, the financials are a band, a headline number, or nothing at all.',
    detail:
      'Turnover with no gross profit will not carry a valuation conversation. So the analyst pulls the accounts by hand anyway, one target at a time.',
  },
  {
    num: '03',
    icon: <NoTraceIcon />,
    title: 'No trace back to the source, the date or the filing',
    lead: 'Most platforms show a value and not where it came from. No registry, no timestamp, no document reference.',
    detail:
      'Ask how old the number is and nobody can say. A figure that cannot be traced gets checked by hand, and then it has stopped saving anyone time.',
  },
  {
    num: '04',
    icon: <OwnershipLayersIcon />,
    title: 'Ownership stops at the shareholder of record',
    lead: 'The name on the register is often a holding company in another jurisdiction, itself held by two more.',
    detail:
      'Who actually controls the company sits three layers up and one border across. By hand that is a week per target, and stale on arrival.',
  },
];

export const InvestmentScreeningProblem = () => {
  const { t } = useInt();
  const headRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...cardRefs.current].filter((el): el is HTMLDivElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section className={styles.root}>
      <div className={styles.aurora} aria-hidden="true">
        <span className={styles.blobOne} />
        <span className={styles.blobTwo} />
      </div>

      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock
          align="left"
          title={t('A screen is only as good as the record underneath it')}
          description={t('Four places where the data most deal teams licence runs out.')}
        />
      </div>

      <div className={styles.grid}>
        {PROBLEMS.map((problem, i) => (
          <div
            key={problem.num}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            className={cn(styles.card, styles.reveal)}
            style={{ transitionDelay: `${i * 110}ms` }}
          >
            <span className={styles.ghostNum}>{problem.num}</span>

            <div className={styles.iconWrap}>{problem.icon}</div>

            <h3 className={styles.cardTitle}>{t(problem.title)}</h3>
            <p className={styles.cardLead}>{t(problem.lead)}</p>
            <p className={styles.cardDetail}>{t(problem.detail)}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};
